import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Briefcase, TrendingUp, AlertOctagon, ShieldAlert, AlertTriangle, CalendarClock, ArrowUpRight } from "lucide-react";

const healthColor = { blue: "#3B82F6", green: "#10B981", yellow: "#F59E0B", red: "#EF4444", gray: "#94A3B8" };

const KINDS = {
  projects: { label: "All projects", icon: Briefcase, tint: "#0044FF" },
  progress: { label: "Progress by project", icon: TrendingUp, tint: "#0044FF" },
  off_track: { label: "Off-track projects", icon: AlertOctagon, tint: "#EF4444" },
  flagged: { label: "At Risk / Jeopardy tasks", icon: ShieldAlert, tint: "#F59E0B" },
  risks: { label: "Open risks & issues", icon: AlertTriangle, tint: "#F59E0B" },
  trueups: { label: "True-ups due", icon: CalendarClock, tint: "#F59E0B" },
};

function Row({ onClick, testid, children }) {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/30 text-left group" data-testid={testid}>
      {children}
      <ArrowUpRight className="h-3.5 w-3.5 shrink-0 opacity-0 group-hover:opacity-70 ml-auto" />
    </button>
  );
}

export default function PortfolioStatDialog({ kind, data, onOpenChange }) {
  const nav = useNavigate();
  if (!kind || !data) return null;
  const cfg = KINDS[kind];
  const Icon = cfg.icon;
  const go = (path) => { onOpenChange(false); nav(path); };
  const att = data.attention || {};
  const det = data.detail || {};

  let rows = [];
  if (kind === "projects" || kind === "progress") {
    const list = kind === "progress" ? [...data.projects].sort((a, b) => a.avg_percent - b.avg_percent) : data.projects;
    rows = list.map((p) => (
      <Row key={p.id} onClick={() => go(`/projects/${p.id}`)} testid={`pstat-project-${p.id}`}>
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: healthColor[p.health] || "#94A3B8" }} />
        <span className="text-sm font-medium min-w-0 flex-1 truncate">{p.name}</span>
        <div className="h-1.5 w-28 rounded-full bg-secondary overflow-hidden shrink-0"><div className="h-full rounded-full" style={{ width: `${p.avg_percent}%`, background: "#0044FF" }} /></div>
        <span className="text-xs font-bold tabular-nums w-10 text-right shrink-0">{p.avg_percent}%</span>
        <span className="text-xs text-muted-foreground w-16 text-right shrink-0">{p.done_count}/{p.task_count}</span>
      </Row>
    ));
  } else if (kind === "off_track") {
    rows = (att.off_track || []).map((p) => (
      <Row key={p.id} onClick={() => go(`/projects/${p.id}`)} testid={`pstat-offtrack-${p.id}`}>
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: "#EF4444" }} />
        <span className="text-sm font-medium min-w-0 flex-1 truncate">{p.name}</span>
        <span className="text-xs text-red-500 font-semibold shrink-0">{p.count} off-track task{p.count === 1 ? "" : "s"}</span>
      </Row>
    ));
  } else if (kind === "flagged") {
    rows = (att.flagged || []).map((f) => (
      <Row key={f.task_id} onClick={() => go(`/projects/${f.project_id}?tab=plan`)} testid={`pstat-flagged-${f.task_id}`}>
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0" style={f.track_status === "jeopardy" ? { color: "#EF4444", background: "#EF44441a" } : { color: "#F59E0B", background: "#F59E0B1a" }}>
          {f.track_status === "jeopardy" ? "Jeopardy" : "At Risk"}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{f.task_title}</p>
          <p className="text-xs text-muted-foreground truncate">{f.project_name}{f.end_date ? ` · due ${f.end_date}` : ""}{f.risk_title ? ` · ↳ ${f.risk_title}` : ""}</p>
        </div>
      </Row>
    ));
  } else if (kind === "risks") {
    rows = (det.open_risks || []).map((r) => (
      <Row key={r.id} onClick={() => go(`/projects/${r.project_id}?tab=risks`)} testid={`pstat-risk-${r.id}`}>
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase shrink-0" style={r.kind === "issue" ? { color: "#EF4444", background: "#EF44441a" } : { color: "#F59E0B", background: "#F59E0B1a" }}>{r.kind}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{r.title}</p>
          <p className="text-xs text-muted-foreground truncate">{r.project_name}{r.owner ? ` · ${r.owner}` : ""}</p>
        </div>
        {r.score != null && <span className="text-xs font-bold shrink-0" style={{ color: r.score >= 15 ? "#EF4444" : r.score >= 8 ? "#F59E0B" : "#10B981" }}>score {r.score}</span>}
      </Row>
    ));
  } else if (kind === "trueups") {
    rows = (att.trueup_due || []).map((p) => (
      <Row key={p.id} onClick={() => go(`/projects/${p.id}`)} testid={`pstat-trueup-${p.id}`}>
        <CalendarClock className="h-4 w-4 text-amber-500 shrink-0" />
        <span className="text-sm font-medium min-w-0 flex-1 truncate">{p.name}</span>
        <span className="text-xs text-amber-600 font-semibold shrink-0">true-up overdue</span>
      </Row>
    ));
  }

  return (
    <Dialog open={!!kind} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="portfolio-stat-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Icon className="h-5 w-5" style={{ color: cfg.tint }} /> {cfg.label}
            <span className="text-sm font-normal text-muted-foreground">({rows.length})</span>
          </DialogTitle>
        </DialogHeader>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10" data-testid="pstat-empty">Nothing here — all clear.</p>
        ) : (
          <div className="divide-y divide-border border border-border rounded-lg overflow-hidden">{rows}</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
